import { createContext, useContext, useMemo, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { BookingApi } from '@/utils/api';
import type { ClinicConfigResponse, ClinicInfo, Service } from '@/types/clinic';

interface ClinicConfigContextValue {
  // Clinic data (full clinic information)
  clinic?: ClinicInfo;
  clinicLoading: boolean;
  clinicError?: unknown;

  // Clinic config (booking settings, theme, etc.)
  config?: ClinicConfigResponse;
  configLoading: boolean;
  configError?: unknown;

  // Services data
  services?: Service[];
  servicesLoading: boolean;
  servicesError?: unknown;

  // Combined loading state
  isLoading: boolean;
  error?: unknown;

  // Methods
  refetchClinic: () => Promise<void>;
  refetchConfig: () => Promise<void>;
  refetchServices: () => Promise<void>;
  clearClinicData: () => void;
}

const ClinicConfigContext = createContext<ClinicConfigContextValue | undefined>(
  undefined
);

// Local storage keys
const CLINIC_DATA_KEY = 'clinic_data';
const CLINIC_CONFIG_KEY = 'clinic_config';
const CLINIC_SERVICES_KEY = 'clinic_services';
const CLINIC_SLUG_KEY = 'clinic_slug';

export const ClinicConfigProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { clinicSlug } = useParams<{ clinicSlug: string }>();
  const [clinic, setClinic] = useState<ClinicInfo | null>(null);
  const [clinicLoading, setClinicLoading] = useState(false);
  const [clinicError, setClinicError] = useState<unknown>(null);

  // Config state management
  const [config, setConfig] = useState<ClinicConfigResponse | null>(null);
  const [configLoading, setConfigLoading] = useState(false);
  const [configError, setConfigError] = useState<unknown>(null);

  // Services state management
  const [services, setServices] = useState<Service[]>([]);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [servicesError, setServicesError] = useState<unknown>(null);

  // Load clinic data and config from localStorage on mount
  useEffect(() => {
    const loadStoredData = () => {
      try {
        const storedSlug = localStorage.getItem(CLINIC_SLUG_KEY);
        const storedClinicData = localStorage.getItem(CLINIC_DATA_KEY);
        const storedConfigData = localStorage.getItem(CLINIC_CONFIG_KEY);
        const storedServicesData = localStorage.getItem(CLINIC_SERVICES_KEY);

        if (storedSlug === clinicSlug) {
          if (storedClinicData) {
            const parsedClinic = JSON.parse(storedClinicData);
            setClinic(parsedClinic);
            console.log(
              'Loaded clinic data from localStorage:',
              parsedClinic.name
            );
          }

          if (storedConfigData) {
            const parsedConfig = JSON.parse(storedConfigData);
            setConfig(parsedConfig);
            console.log('Loaded config from localStorage');
          }

          if (storedServicesData) {
            const parsedServices = JSON.parse(storedServicesData);
            setServices(parsedServices);
            console.log('Loaded services from localStorage');
          }
        }
      } catch (error) {
        console.error('Error loading stored data:', error);
        // Clear corrupted data
        localStorage.removeItem(CLINIC_DATA_KEY);
        localStorage.removeItem(CLINIC_CONFIG_KEY);
        localStorage.removeItem(CLINIC_SERVICES_KEY);
        localStorage.removeItem(CLINIC_SLUG_KEY);
      }
    };

    loadStoredData();
  }, [clinicSlug]);

  // Save clinic data and config to localStorage when they change
  useEffect(() => {
    if (clinic && clinicSlug) {
      try {
        localStorage.setItem(CLINIC_DATA_KEY, JSON.stringify(clinic));
        localStorage.setItem(CLINIC_SLUG_KEY, clinicSlug);
        console.log('Saved clinic data to localStorage:', clinic.name);
      } catch (error) {
        console.error('Error saving clinic data to localStorage:', error);
      }
    }
  }, [clinic, clinicSlug]);

  useEffect(() => {
    if (config && clinicSlug) {
      try {
        localStorage.setItem(CLINIC_CONFIG_KEY, JSON.stringify(config));
        console.log('Saved config to localStorage');
      } catch (error) {
        console.error('Error saving config to localStorage:', error);
      }
    }
  }, [config, clinicSlug]);

  useEffect(() => {
    if (services.length > 0 && clinicSlug) {
      try {
        localStorage.setItem(CLINIC_SERVICES_KEY, JSON.stringify(services));
        console.log('Saved services to localStorage');
      } catch (error) {
        console.error('Error saving services to localStorage:', error);
      }
    }
  }, [services, clinicSlug]);

  const fetchClinicData = async () => {
    if (!clinicSlug) {
      setClinicError('No clinic slug provided');
      setClinicLoading(false);
      return;
    }

    setClinicLoading(true);
    setClinicError(null);

    try {
      const configData = await BookingApi.getClinicConfigBySlug(clinicSlug);
      // Build minimal clinic object from config
      const cs = (configData.active_theme?.theme?.color_scheme ||
        configData.active_theme?.color_scheme) as unknown as Record<
        string,
        string
      >;
      const minimalClinic = {
        id: String(configData.location.id),
        name: configData.location.name,
        slug: configData.location.slug,
        description: configData.ai_agent?.greeting_message || '',
        address: '',
        phone: '',
        email: '',
        website: undefined,
        theme: {
          primaryColor: cs?.primary || '#2563eb',
          secondaryColor: cs?.secondary || '#1d4ed8',
          accentColor: cs?.main || cs?.primary || '#2563eb',
          backgroundColor: '#FFFFFF',
          textColor: cs?.['text-color'] || '#1F2937',
          mainColor: cs?.primary || '#2563eb',
          gradient: (cs?.gradient as string) ?? null,
          iconStroke: cs?.['icon-stroke'] || cs?.secondary || undefined,
        },
        services: [],
        doctors: [],
        workingHours: {
          monday: { isOpen: false },
          tuesday: { isOpen: false },
          wednesday: { isOpen: false },
          thursday: { isOpen: false },
          friday: { isOpen: false },
          saturday: { isOpen: false },
          sunday: { isOpen: false },
        },
        isActive: true,
        aiAgent: configData.ai_agent,
      } as ClinicInfo;
      setClinic(minimalClinic);
      console.log('Fetched clinic via config');
    } catch (err) {
      setClinicError('Error loading clinic information');
      console.error('Error fetching clinic:', err);
    } finally {
      setClinicLoading(false);
    }
  };

  const fetchConfigData = async () => {
    if (!clinicSlug) {
      setConfigError('No clinic slug provided');
      setConfigLoading(false);
      return;
    }

    setConfigLoading(true);
    setConfigError(null);

    try {
      const configData = await BookingApi.getClinicConfigBySlug(clinicSlug);
      if (configData) {
        setConfig(configData);
        console.log('Fetched fresh config data');
      } else {
        setConfigError('Config not found');
      }
    } catch (err) {
      setConfigError('Error loading config');
      console.error('Error fetching config:', err);
    } finally {
      setConfigLoading(false);
    }
  };

  const fetchServicesData = async () => {
    if (!clinicSlug) {
      setServicesError('No clinic slug provided');
      setServicesLoading(false);
      return;
    }

    setServicesLoading(true);
    setServicesError(null);

    try {
      const response = await BookingApi.getServices(clinicSlug);
      const mapped = response.data?.services?.map(s => ({
        id: String(s.id),
        name: s.name,
        description: s.description,
        duration: 0,
        price: parseFloat(s.pre_payment_amount) || 0,
        category: '',
        translated_name: s.translated_name,
        pre_payment_required: s.pre_payment_required,
        pre_payment_amount: s.pre_payment_amount,
        icon: s.icon
          ? s.icon.startsWith('http')
            ? s.icon
            : `https://core-api.elva.ai${s.icon}`
          : undefined,
      })) as Service[];
      setServices(mapped || []);
      console.log('Fetched fresh services data');
    } catch (err) {
      setServicesError('Error loading services');
      console.error('Error fetching services:', err);
    } finally {
      setServicesLoading(false);
    }
  };

  const refetchClinic = async () => {
    await fetchClinicData();
  };

  const refetchConfig = async () => {
    await fetchConfigData();
  };

  const refetchServices = async () => {
    await fetchServicesData();
  };

  const clearClinicData = () => {
    setClinic(null);
    setConfig(null);
    setServices([]);
    localStorage.removeItem(CLINIC_DATA_KEY);
    localStorage.removeItem(CLINIC_CONFIG_KEY);
    localStorage.removeItem(CLINIC_SERVICES_KEY);
    localStorage.removeItem(CLINIC_SLUG_KEY);
    console.log('Cleared clinic data, config, and services');
  };

  const value = useMemo<ClinicConfigContextValue>(
    () => ({
      clinic: clinic || undefined,
      clinicLoading,
      clinicError,
      config: config || undefined,
      configLoading,
      configError,
      services: services.length > 0 ? services : undefined,
      servicesLoading,
      servicesError,
      isLoading: clinicLoading || configLoading || servicesLoading,
      error: clinicError || configError || servicesError,
      refetchClinic,
      refetchConfig,
      refetchServices,
      clearClinicData,
    }),
    [
      clinic,
      clinicLoading,
      clinicError,
      config,
      configLoading,
      configError,
      services,
      servicesLoading,
      servicesError,
      refetchClinic,
      refetchConfig,
      refetchServices,
      clearClinicData,
    ]
  );

  return (
    <ClinicConfigContext.Provider value={value}>
      {children}
    </ClinicConfigContext.Provider>
  );
};

export const useClinicConfigContext = (): ClinicConfigContextValue => {
  const ctx = useContext(ClinicConfigContext);
  if (!ctx) {
    throw new Error(
      'useClinicConfigContext must be used within ClinicConfigProvider'
    );
  }
  return ctx;
};
